import type { BaseItemSchema } from "@memorang/types";
import { useCallback } from "react";
import { isMultiSelect as isMultiSelectItem } from "../helpers/item-type-guards";
import {
	getCorrectAnswerIdsFromAnswerKey,
	getNumChoicesRequired,
} from "../helpers/session";
import useItemAnsweredState from "./useItemAnsweredState";
import useSessionStore from "./useSessionStore";

const useChoiceInteraction = (item: BaseItemSchema) => {
	const updateItemStates = useSessionStore((s) => s.updateItemStates);
	const itemStates = useSessionStore((s) => s.itemStates);

	const { isAnswered, inReviewMode, isSummativeTest } =
		useItemAnsweredState(item);

	const itemId = item.id!;
	const { scoring, response } = item;

	const choices = response.type === "OPTION_SELECTION" ? response.choices : [];

	const isSelectionScoring =
		scoring?.type === "ANSWER_KEY_SELECTION" || scoring?.type === "choice";
	const correctAnswerIds = isSelectionScoring
		? getCorrectAnswerIdsFromAnswerKey(choices, scoring!)
		: undefined;
	const numChoicesRequired = isSelectionScoring
		? getNumChoicesRequired(scoring!)
		: undefined;
	const isMultiSelect = isMultiSelectItem(item);

	const itemState = itemStates?.get(itemId);
	const selectedChoiceIds =
		itemState?.selectedChoiceIds ?? item.answer?.selectedChoiceIds;

	const handleSelectChoice = useCallback(
		(choiceId: string, crossingOut?: boolean) => {
			updateItemStates({
				itemId,
				selectedChoiceId: choiceId,
				multiSelect: isMultiSelect,
				crossOut: crossingOut,
			});
		},
		[itemId, isMultiSelect, updateItemStates],
	);

	return {
		choices,
		correctAnswerIds,
		numChoicesRequired,
		isMultiSelect,
		isAnswered,
		inReviewMode,
		isSummativeTest,
		selectedChoiceIds,
		handleSelectChoice,
	};
};

export default useChoiceInteraction;
